import { PagingOptions } from "./types";

export interface Cluster {
  code: string;
  description: string;
}

export interface Department {
  code: string;
  description: string;
}

export interface DrivingTime {
  HasValue: boolean;
  DateTime?: Date;
  TimeDifference: string;
}

export interface PrimarySurveyV2 {
  Code: string;
  DisplayName: string;
  Groups: PrimarySurveyV2Group[];
}

export interface PrimarySurveyV2Group {
  Index: number;
  Title: string;
  Items: PrimarySurveyV2GroupItem[];
}

export interface PrimarySurveyV2GroupItem {
  Index: string;
  Title: string;
  Value: string;
}

export interface SecundarySurveyV2 {
  Code: string;
  DisplayName: string;
  Groups: SecundarySurveyV2Group[];
}

export interface SecundarySurveyV2Group {
  Index: number;
  Title: string;
  Items: SecundarySurveyV2GroupItem[];
}

export interface SecundarySurveyV2GroupItem {
  Index: string;
  Title: string;
  Value: string;
}

export interface MedicalCondition {
  Index: number;
  Code: string;
  ClinicalTerm: string;
}

export interface NamedValue {
  Id: number;
  Name: string;
  Value: string;
}

export interface CodedValue {
  Id: number;
  Code: string;
  Value: string;
}

export interface SecondarySurvey extends NamedValue {}

export interface MedicationAdministration extends NamedValue {
  Code: string;
  RouteCodeName?: string;
  StartTime?: Date;
  EndTime?: Date;
  Description?: string;
  AdditionalText?: string;
}

export interface Sign extends NamedValue {
  Timestring: string;
  Unit: string;
  DenominatorValue: string;
  DenominatorUnit: string;
  Values: Array<SignValue>;
}

export interface SignValue {
  Timestring: string;
  Value: string;
}

export interface Observation extends CodedValue {}

export interface ObservationHeader {
  Header: string;
  ObservationModels: Observation[];
}

export interface SubstanceAdministration {
  Name: string;
  Product?: string;
  Rate?: string;
}

export interface TreatmentProcedure {
  Id: number;
  Name: string;
  HasDetailInformation: boolean;
  SubstanceAdministrations: Array<SubstanceAdministration>;
}

export interface Attachment {
  Id: number;
  SetId: string;
  VersionNumber: string;
  DateTime: Date;
  DisplayName: string;
  Text: string;
  Download: FileDownloadModel;
  Open: FileDownloadModel;
}

export interface FileDownloadModel {
  Action: string;
  Parameters: Record<string, object>;
  FileType: string;
}

export interface ReferralModel {
  URN: string;
  Type: string;
  Reference: string;
  State: string;
  Status: string;
  UrgencyCode: string;
  PatientId: string;
  PatientName: string;
  PatientNumber?: string;
  PatientNumberVerificationLink?: string;
  PatientNumberStatusMessage?: string;
  PatientBirthDate?: Date;
  Gender?: string;
  MessageDateTime?: Date;
  MessageDate?: string;
  MessageTime?: string;
  EtaDate?: string;
  EtaTime?: string;
  AmbulanceId?: string;
  DepartureLocation?: string;
  DeparturePostalCode?: string;
  ClusterCode?: string;
  Department?: string;
  DepartmentCode?: string;
  RequiredSpecialism?: string;
  Injury?: string;
  IsHighlighted: boolean;
  IsCancelled: boolean;
  IsDeprecatedVersion: boolean;
  SendingEntity: string;
  HasAmbulanceFeedback: boolean;
  HasExternalMonitorApi: boolean;
  HasPreviousMessage: boolean;
  AmbulanceNurseIdentificationNumber?: string;
  AmbulanceDriverIdentificationNumber?: string;
  IsOpened: boolean;
}

export interface ReferralDetailsModel {
  ReferralId?: number;
  Reference: string;
  State: string;
  TransportationId?: number;
  PatientNumber: string;
  Espoed: ReferralEspoedDetailsModel;
  Triage: ReferralTriageDetailsModel;
  RelatedReferrals: RelatedReferralModel[];
}

export interface RelatedReferralModel {
  ReferenceId?: string;
  MessageDateTime?: Date;
  MessageType?: number;
  UrgencyCode?: string;
  ReferralStatus?: string;
  IsInSlidingWindow?: boolean;
}

export interface ReferralEspoedDetailsModel extends ReferralModel {
  ReferralId?: number;
  TransportationId?: number;

  AmbulanceUrgencyCode: string;
  EmergencyRoomUrgencyCode: string;
  PatientBSN: string;
  PatientBirthDate?: Date;
  PatientAge?: number;
  PatientAddressLine1: string;
  PatientAddressLine2: string;
  PatientPhoneNumber: string;
  PatientMailAddress: string;
  InsuranceValid: boolean;
  ScopingOrganizationName: string;
  ScopingOrganizationAddress: string;
  ScopingOrganizationTel: string;

  PassportNumber: string;
  DriversLicenseNumber: string;
  IDCardNumber: string;
  AliensDocumentNumber: string;

  ContactRoleTypeCode: string;
  ContactRoleTypeDisplayName: string;
  ContactName: string;
  ContactPhoneNumber: string;

  CallInTime: DrivingTime;
  DepartureTime: DrivingTime;
  ArrivalAtPatientTime: DrivingTime;
  PatientDepartureTime: DrivingTime;
  EndTime: DrivingTime;
  EstimatedTimeOfArrival: DrivingTime;
  TotalDrivingTime: string;

  SecondarySurveys: SecondarySurvey[];
  PrimarySurveyV2?: PrimarySurveyV2;
  SecundarySurveyV2?: SecundarySurveyV2;
  MedicationAdministrations: MedicationAdministration[];
  Mechanisms: string[];
  MechanismObservations: Observation[];
  InjuryObservations: Observation[];
  InjuryAdditionalInformation: string;
  Signs: Sign[];
  TreatmentProcedures: TreatmentProcedure[];
  Attachments: Attachment[];
  TreatmentAdditionalInformation: string;
  MedicalConditions: MedicalCondition[];

  EditPatientNumberBaseURI: string;
  ManualCloseEnabled: boolean;
  ReferralStatus: number;
  PatientNumberEditEnabled: boolean;
  InboxNotificationsRepeatUntilReadAll: boolean;
  GeneralPractitionerName: string;
  GeneralPractitionerAgbCode: string;
  MedicalNotes?: string;
  GeneralComment?: string;
  BodyPosition?: string;
  RawMessage?: string;

  WzdWvggzMeasure?: string;
  StartTimeInvoluntaryCare?: Date;
  CareRequesterType?: string;

  PatientConsentedToClinicalTrials: string[];
  ConsentInformRelatives?: string;

  AgreementsWithPatient?: AgreementsWithPatientModel;
  ConsultingExternalProfessionalModel?: ConsultingExternalProfessional;

  SendingEntity: string;

  AmbuFeedbackMessageLogs: AmbuFeedbackMessageLogModel[];
}

export interface AmbuFeedbackMessageLogModel {
  Id: number;
  IncomingRawMessage: string;
  IncomingTime: Date;
  OutgoingRawMessage: string;
  OutgoingTime: Date;
  OutgoingReference: string;
  EffectiveDateTime?: Date;
  ReferralReferenceId?: string;
  PatientBsn?: string;
  PatientGender?: string;
  PatientBirthdate?: Date;
  DiagnosisMatch?: boolean;
  ProblemObservations: ProblemObservationModel[];
  MedicalRemarks?: string;
  CommunicationRemarks?: string;
  DiagnosisTimestamp?: Date;
  AuthorId?: string;
  AuthorName?: string;
}

export interface ProblemObservationModel {
  Index: number;
  CodeSystem?: string;
  CodeSystemName?: string;
  Code?: string;
  DisplayName?: string;
  AdditionalText?: string;
}

export interface ReferralTriageDetailsModel extends ReferralModel {
  ReferralId?: number;
  TransportationId?: number;
  RawMessage?: string;
  EditPatientNumberBaseURI: string;
  PatientNumberEditEnabled: boolean;
  PatientBSN: string;
  PatientBirthDate?: Date;
  PatientAge?: number;
  PatientAddressLine1: string;
  PatientAddressLine2: string;
  PatientAddressLine3: string;
  PatientPhoneNumber: string;
  ContactRoleTypeCode: string;
  ContactRoleTypeDisplayName: string;
  ContactName: string;
  ContactPhoneNumber: string;
  GeneralPractitionerName: string;
  GeneralPractitionerAgbCode: string;
  GeneralPractitionerPhoneNumber: string;
  GeneralPractitionerOrganizationName: string;
  ManualCloseEnabled: boolean;
  StartTime: DrivingTime;
  ArrivalAtPatientTime: DrivingTime;
  PatientDepartureTime: DrivingTime;
  EndTime: DrivingTime;
  EstimatedTimeOfArrival: DrivingTime;
  TotalDrivingTime: string;
  ReferralInfo: TriageReferralInfo;
  GeneralComments: string[];
  ReferralTo: TriageReferralTo;
  Triage: TriageData;
  ProfessionalSummaryGeneralPractitionerObservationFile: TriageObservationFile;
  SummarizationEncounterNoteNarrativeObservationFile: TriageObservationFile;
  AllObservationAlerts: ObservationAlert[];
  AllAllergyIntolerances: ObservationAllergyIntolerance[];
  Overseer: Overseer;
}

export interface ConsultingExternalProfessional {
  Consultations: ExternalConsultation[];
}

export interface ExternalConsultation {
  Identification: string;
  IdentificationIsUzi: boolean;
  IdentificationIsAgb: boolean;
  Role: string;
  Address: string;
  Telecom: string;
  GivenName: string;
  FamilyName: string;
  AgreementsWithExternalProfessional: string;
}

export interface AgreementsWithPatientModel {
  GeneralPractitionerInformed: boolean;
  DescriptionOfTheAgreementsMadeWithThePatient: string;
}

export interface Overseer {
  OverseerUziCode: string;
  OverseerName: string;
  OverseerNameWithUziCode: string;
  RoleCode: string;
  RoleDisplayName: string;
  RoleDisplayValue: string;
  OrganizationUraCode: string;
  OrganizationName: string;
  OrganizationDisplayValue: string;
  OrganizationStreetName: string;
  OrganizationHouseNumber: string;
  OrganizationBuildingNumberSuffix: string;
  OrganizationPostalCode: string;
  OrganizationCity: string;
  OrganizationCountry: string;
  OverseerAddressLine: string;
}

export interface TriageReferralInfo {
  ReferrerUziCode: string;
  ReferrerNameInitials: string;
  ReferrerNameFamilyName: string;
  ReferrerFullName: string;
  ReferrerFullNameWithUziCode: string;
  ReferrerOrganizationUraCode: string;
  ReferrerOrganizationName: string;
  Reason: string;
  Diagnosis: string;
  EffectiveTime?: Date;
  ReferrerBusinessRoleCode: string;
  ReferrerBusinessRoleDisplayName: string;
  ReferrerBusinessPhoneNumber: string;
}

export interface TriageReferralTo {
  SpecialismCode: string;
  SpecialismDisplayName: string;
  SpecialismDisplayText: string;
  OrganizationDepartment: string;
  Particularity: string;
}

export interface TriageData {
  TriageSystemCode: string;
  TriageSystemDisplayName: string;
  NtsUrgencyCode: string;
  NtsUrgencyCodeDisplayName: string;
  Acuity: string;
  ABCDSafe: string;
  TriagistUrgencyCode: string;
  TriagistUrgencyCodeDisplayName: string;
  TriagistUrgencyCodeDeviationReasonCode: string;
  TriagistUrgencyCodeDeviationReasonDisplayName: string;
  TriagistUrgencyCodeDeviationReasonExplanation: string;
  TriageExplanation: string;
  AcuityAssessmentSkipReason: string;
  TriageTypeDisplayName: string;
  EntryComplaints: string;
  Observations: TriageObservation[];
  ABCDObservations: TriageObservation[];
  NormalObservations: TriageObservation[];
  NotificationDateTime?: Date;
  PerformerStartDateTime?: Date;
  PerformerEndDateTime?: Date;
}

export interface TriageObservation {
  Code: string;
  CodeTitle: string;
  CodeExplanation: string;
  Value: string;
  ValueTitle: string;
  ValueExplanation: string;
}

export interface ContactReport {
  NotesNarrativesHealthCareProfessionalUziCode: string;
  NotesNarrativesHealthCareProfessionalName: string;
  NotesNarratives: TriageNoteNarrative[];
  PlanOfTreatmentNarrative: string;
  ObjectiveNarrative: string;
  SubjectiveNarrative: string;
  EvaluationNote: string;
  EvaluationNoteSingleLine: string;
  EvaluationNoteObservationValue: string;
  Episode: string;
}

export interface TriageObservationFile {
  ContactKindDisplayValue: string;
  ContactReports: ContactReport[];
  DiagnosticObservations: DiagnosticObservation[];
  SubstanceAdministrations: TriageSubstanceAdministration[];
  Diagnosis: Diagnosis[];
  NoteCategories: TriageNoteCategory[];
  AllergyIntolerances: ObservationAllergyIntolerance[];
  Alerts: ObservationAlert[];
  ReferralUrgencyCode: string;
  ReferralUrgencyCodeDisplayName: string;
}

export interface TriageNoteNarrative {
  TemplateId: string;
  Code: string;
  CodeDisplayName: string;
  Text: string;
  Observation: TriageObservation;
}

export interface DiagnosticObservation {
  HealthCareProfessionalName: string;
  HealthCareProfessionalUziCode: string;
  EffectiveTime?: Date;
  Code: string;
  CodeDisplayName: string;
  CodeExplanationText: string;
  Value: string;
  Unit: string;
  InterpretationCode: string;
  InterpretationDisplayName: string;
  ReferenceRange: ReferenceRange;
  GroupCode: string;
  GroupTypeIndex: number;
}

export interface ReferenceRange {
  LowValue: string;
  LowUnit: string;
  LowNullFlavorCode: string;
  LowNullFlavorDisplayName: string;
  HighValue: string;
  HighUnit: string;
  HighNullFlavorCode: string;
  HighNullFlavorDisplayName: string;
  DisplayValue: string;
}

export interface TriageSubstanceAdministration {
  Code: string;
  CodeDisplayName: string;
  Text: string;
  PrescriberName: string;
  PrescriberUziCode: string;
  PrescriptionDateTime?: Date;
  PrescriptionDateTimeNullFlavor: string;
  PrescriptionReasonValueCode: string;
  PrescriptionReasonValueDisplayValue: string;
  HPKCode: string;
  HPKDisplayName: string;
  PRKCode: string;
  PRKDisplayName: string;
  GPKCode: string;
  GPKDisplayName: string;
  AdditionalText: string;
  UsagePeriod: string;
  ManufacturedMaterial: string;
  SimpleManufacturedMaterial: string;
}

export interface Diagnosis {
  Text: string;
  Code: string;
  CodeDisplayName: string;
  Value: string;
  ValueDisplayName: string;
  StatusCode: string;
}

export interface TriageNoteCategory {
  StartDateTime?: Date;
  EndDateTime?: Date;
  AssignedAuthorInitials: string;
  AssignedAuthorFamilyName: string;
  FullName: string;
  Code: string;
  CodeDisplayName: string;
  Text: string;
}

export interface ObservationAllergyIntolerance {
  EffectiveTime?: Date;
  ValueCode: string;
  ValueCodeDisplayName: string;
  Code: string;
  CodeDisplayName: string;
  OriginalText: string;
}

export interface ObservationAlert {
  EffectiveTime?: Date;
  Code: string;
  CodeDisplayName: string;
  DataSource: ProfessionalSummaryOrganizationAndPatient;
}

export interface PatientInfoModel {
  FamilyName: string;
  GivenName: string;
  BirthDate?: Date;
  Gender: string;
  MultipleBirthIndicator: boolean;
  Street: string;
  HouseNumber: string;
  City: string;
  PostalCode: string;
  PatientNumber: string;
  Bsn: string;
  Country: string;
}
export class ReferralLoadOptions {
  public cluster: string;
  public department: string;
  public type: string;
  public priority: string;
  public pagingOptions: PagingOptions;
  public patientNumbers?: string[];

  constructor(
    cluster: string,
    department: string,
    type: string,
    priority: string,
    pagingOptions: PagingOptions,
    patientNumbers?: string[],
  ) {
    this.cluster = cluster;
    this.department = department;
    this.type = type;
    this.priority = priority;
    this.pagingOptions = pagingOptions;
    this.patientNumbers = patientNumbers;
  }
}

export class ReferralSearchOptions {
  public cluster: string;
  public department: string;
  public type: string;
  public priority: string;
  public filterText?: string;
  public dateFrom?: string;
  public dateTo?: string;
  public pagingOptions: PagingOptions;

  constructor(
    cluster: string,
    department: string,
    type: string,
    priority: string,
    pagingOptions: PagingOptions,
    filterText?: string,
    dateFrom?: string,
    dateTo?: string,
  ) {
    this.cluster = cluster;
    this.department = department;
    this.type = type;
    this.priority = priority;
    this.filterText = filterText;
    this.dateFrom = dateFrom;
    this.dateTo = dateTo;
    this.pagingOptions = pagingOptions;
  }
}

export interface ProfessionalSummaryDetailsModel {
  ResponseDate?: Date;
  Alerts: ObservationAlert[];
  DiagnosticObservations: DiagnosticObservation[];
  AllergyIntolerances: ObservationAllergyIntolerance[];
  SubstanceAdministrations: SubstanceAdministration[];
  encounters: Encounter[];
  contactReports: ContactReport[];
  Diagnosis: Diagnosis[];
  RawMessage?: string;
  ProfessionalSummaryOrganizationsAndPatients: ProfessionalSummaryOrganizationAndPatient[];
  AcknowledgementDetails: AcknowledgementDetails[];
  // Raw HL7v3 queryAck.queryResponseCode: "OK" (data found), "NF" (no data found),
  // "AE" (application error) or "QE" (query parameter error).
  QueryResponseCode?: "OK" | "NF" | "AE" | "QE";
}

export interface ProfessionalSummaryOrganizationAndPatient {
  Index: number;
  PatientModel: PatientInfoModel;
  OrganizationName: string;
  OrganizationApplicationId: string;
}

export interface ContactReport {
  HealthCareOrganisation: string;
  HealthCareProfessionalName: string;
  HealthCareProfessionalUziCode: string;
  EffectiveTime?: Date;
  SubjectiveNarrative: string;
  ObjectiveNarrative: string;
  EvaluationNote: string;
  PlanOfTreatment: string;
  Diagnosis: Diagnosis;
}

export interface Encounter {
  HealthCareOrganisation: string;
  HealthCareProfessionalName: string;
  HealthCareProfessionalUziCode: string;
  EffectiveTime?: Date;
  Code: string;
  CodeDisplayName: string;
}

export interface AcknowledgementDetails {
  TypeCode: string;
  Code: string;
  CodeDisplayName: string;
}

export interface MandateGroupListModel {
  Id: number;
  Created: Date;
  Updated: Date;
  Name: string;
  GroupOwnerUziNumber: string;
  GroupOwnerFullName: string;
  GroupOwnerRoleCode: string;
  Members: MandateGroupMemberListModel[];
  Tokens: MandateGroupTokenListModel[];
  showAddMember: boolean;
  showCreateNewMandate: boolean;
}

export interface MandateGroupMemberListModel {
  Id: number;
  Created: Date;
  Updated: Date;
  UziNumber: string;
  FullName: string;
}

export interface MandateGroupTokenListModel {
  Id: number;
  Created: Date;
  Updated: Date;
  ExpirationDate: Date;
  Revoked: boolean;
  RevokeDateTime: Date;
}

export interface ExternalMonitorLinkModel {
  Manufacturer: string;
  DeviceSerialNumber: string;
  /** The care organisation's own asset tag on the device, e.g. "UAT0143". */
  InventoryNumber: string | null;
  DeviceName: string;
  /** Whether the device is connected. Null when the platform does not report it. */
  IsOnline: boolean | null;
  /** Connectivity in the platform's own words, e.g. "offline". */
  DeviceStatus: string | null;
  DeviceModel: string | null;
  SoftwareVersion: string | null;
  /** Mission start from the device clock — display only, it is not reliable. */
  MissionStart: string | null;
  AmbulanceNumber: string;
  MonitorUrl: string;
  /** Embeddable live view, rendered in an iframe. Null when the manufacturer has none. */
  WidgetUrl: string | null;
  AEcgXml: string | null;
}
